import { useContext, useState, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import SalesContext from "context/SalesContext";
import ProductContext from "context/ProductContext";
import ShopContext from "context/ShopContext";
import ScannedItemsDialog from "components/popups/ScannedItemsDialog/ScannedItemsDialog";
import OnlineOrderDetailsPopup from "components/popups/OnlineOrderDetailsPopup/OnlineOrderDetailsPopup";
import SearchContext from "context/SearchContext";
import { useBarcodeScanner, tryParseOnlineOrderScan, processScannedBarcodeItems } from "helpers/BarcodeHelper";

function openSelectableMatchDialog(selectableMatch, selectedShop, setMatchedItems, setCurrentQuantity, setDialogOpen) {
  if (!selectableMatch.value) return;

  const { matches, qty } = selectableMatch.value;

  setMatchedItems(
    matches.map((match) => ({
      ...match,
      quantity: qty,
      currency_symbol: selectedShop?.currency_symbol || "",
    })),
  );

  setCurrentQuantity(qty);
  setDialogOpen(true);
}

function showBarcodeNotFoundError(uniqueItemsToAdd, selectableMatch) {
  if (uniqueItemsToAdd.length === 0 && !selectableMatch.value) {
    toast.error("Product not found");
  }
}

async function processBarcodeScanPayload(
  scannedCode,
  {
    setOnlineOrderId,
    setOnlineOrderSecret,
    setOnlineOrderOpen,
    availableItems,
    handleBatchAddItemsToSale,
    selectedShop,
    setMatchedItems,
    setCurrentQuantity,
    setDialogOpen,
  },
) {
  const trimmedCode = scannedCode.trim();

  if (tryParseOnlineOrderScan(trimmedCode, setOnlineOrderId, setOnlineOrderSecret, setOnlineOrderOpen)) {
    return { success: true, type: "online_order" };
  }

  const uniqueItemsToAdd = [];
  const selectableMatch = { value: null };

  await processScannedBarcodeItems(
    trimmedCode,
    availableItems,
    uniqueItemsToAdd,
    selectableMatch,
    handleBatchAddItemsToSale,
  );
  openSelectableMatchDialog(selectableMatch, selectedShop, setMatchedItems, setCurrentQuantity, setDialogOpen);
  showBarcodeNotFoundError(uniqueItemsToAdd, selectableMatch);

  const isMatched = uniqueItemsToAdd.length > 0 || Boolean(selectableMatch.value);
  return { success: isMatched, uniqueItemsToAdd, selectableMatch };
}

const BarcodeScanner = ({ isDisabled = false, onScanStart = null, onPreScan = null, onScanSuccess = null }) => {
  const { handleAddItemToSale, handleBatchAddItemsToSale } = useContext(SalesContext);
  const { availableItems } = useContext(ProductContext);
  const { selectedShop } = useContext(ShopContext);
  const { setSearchTerm } = useContext(SearchContext);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [matchedItems, setMatchedItems] = useState([]);
  const [currentQuantity, setCurrentQuantity] = useState(1);

  const [onlineOrderOpen, setOnlineOrderOpen] = useState(false);
  const [onlineOrderId, setOnlineOrderId] = useState(null);
  const [onlineOrderSecret, setOnlineOrderSecret] = useState(null);

  const handleScanCode = useCallback(
    async (scannedCode) => {
      if (!scannedCode) return;
      const trimmedCode = scannedCode.trim();
      if (!trimmedCode) return;

      if (onPreScan) {
        await onPreScan(trimmedCode);
      }

      const scanResult = await processBarcodeScanPayload(trimmedCode, {
        setOnlineOrderId,
        setOnlineOrderSecret,
        setOnlineOrderOpen,
        availableItems,
        handleBatchAddItemsToSale,
        selectedShop,
        setMatchedItems,
        setCurrentQuantity,
        setDialogOpen,
      });

      if (setSearchTerm) {
        setSearchTerm("");
      }

      if (scanResult?.success && onScanSuccess) {
        onScanSuccess(scanResult);
      }
    },
    [availableItems, handleBatchAddItemsToSale, selectedShop, setSearchTerm, onPreScan, onScanSuccess],
  );

  const scannerOptions = useMemo(
    () => ({
      isDisabled: isDisabled || dialogOpen || onlineOrderOpen,
      onScanStart,
    }),
    [isDisabled, dialogOpen, onlineOrderOpen, onScanStart],
  );

  useBarcodeScanner(handleScanCode, scannerOptions);

  const handleSelectItem = (item) => {
    handleAddItemToSale(item, item.quantity || currentQuantity, "", item.sale_price ?? item.price ?? null);
    setDialogOpen(false);
    if (onScanSuccess) {
      onScanSuccess({ success: true, item });
    }
  };

  return (
    <>
      <ScannedItemsDialog
        open={dialogOpen}
        items={matchedItems}
        onSelect={handleSelectItem}
        onClose={() => setDialogOpen(false)}
      />
      <OnlineOrderDetailsPopup
        open={onlineOrderOpen}
        orderId={onlineOrderId}
        secret={onlineOrderSecret}
        onClose={() => {
          setOnlineOrderOpen(false);
          setOnlineOrderId(null);
          setOnlineOrderSecret(null);
        }}
      />
    </>
  );
};

export default BarcodeScanner;
